/**
 * Construction Notification Worksheet — assembles the data needed to file the FCC
 * Form 601 Schedule K construction notification for a license, and lets staff record
 * that they filed it.
 *
 * Rendered as a standalone document (outside the app Layout) so browser print /
 * "Save as PDF" produces a clean page. Pulls GET /licenses/{callSign}/construction-worksheet.
 *
 * It is an internal prep sheet, never something to send a licensee. On 2026-10-21 a
 * print of it went to a school cold, and our logo beside "FCC Form 601" read as an
 * official notice. So it says "internal" in print and in the tab title (which browsers
 * print in the page header), keeps the logo small, and its "i" explainer (open on the
 * first visit) points staff at the courtesy notice e-mail instead.
 */
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getConstructionWorksheet, markConstructionFiled } from '../services/licenses'
import type { ConstructionWorksheet, ConstructionWorksheetObligation } from '../types'
import PageHeading from '../components/PageHeading'
import { CONSTRUCTION_WORKSHEET_HELP as HELP } from '../content/pageHelp'

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  try {
    const d = new Date(dateStr)
    return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`
  } catch {
    return dateStr
  }
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '—'
  try { return new Date(dateStr).toLocaleString() } catch { return dateStr }
}

const STATUS_STYLE: Record<string, string> = {
  overdue: 'bg-red-100 text-red-800',
  open: 'bg-amber-100 text-amber-800',
  built: 'bg-green-100 text-green-800',
  unknown: 'bg-gray-100 text-gray-600',
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[status] || STATUS_STYLE.unknown}`}>
      {status}
    </span>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm text-gray-900">{children ?? '—'}</div>
    </div>
  )
}

function oblLocation(o: ConstructionWorksheetObligation): string {
  return [o.location_address, o.location_city, o.location_county && `${o.location_county} Co.`, o.location_state]
    .filter(Boolean).join(', ') || '—'
}

export default function ConstructionWorksheetPage() {
  const { callSign } = useParams<{ callSign: string }>()
  const [ws, setWs] = useState<ConstructionWorksheet | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notFound, setNotFound] = useState(false)

  // Filing form
  const [filingRef, setFilingRef] = useState('')
  const [savingFiled, setSavingFiled] = useState(false)

  useEffect(() => {
    if (!callSign) return
    let cancelled = false
    setLoading(true); setError(''); setNotFound(false)
    getConstructionWorksheet(callSign)
      .then((data) => { if (!cancelled) { setWs(data); setFilingRef(data.filing_reference || '') } })
      .catch((e: Error) => {
        if (cancelled) return
        if (e.message.includes('404') || e.message.toLowerCase().includes('not found')) setNotFound(true)
        else setError(e.message || 'Failed to load worksheet')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [callSign])

  // The browser prints the page title in its header, so make that say "internal" too.
  useEffect(() => {
    if (!callSign) return
    const previous = document.title
    document.title = `Internal worksheet — ${callSign} — not for the licensee`
    return () => { document.title = previous }
  }, [callSign])

  async function toggleFiled(filed: boolean) {
    if (!callSign) return
    // Warn (but allow) when filing a 601 for a license not linked to one of our clients —
    // you normally only file on behalf of an entity you have a business relationship with.
    if (filed && ws && !ws.linked_client_id) {
      const ok = window.confirm(
        `${ws.licensee_name || ws.call_sign} is not linked to a client in your CRM.\n\n` +
        `You'd normally only file a Form 601 construction notification for an entity you have a ` +
        `business relationship with. Mark it as filed anyway?`,
      )
      if (!ok) return
    }
    setSavingFiled(true)
    setError('')
    try {
      const res = await markConstructionFiled(callSign, filed, filed ? filingRef : undefined)
      setWs(prev => prev ? {
        ...prev,
        filed: res.filed,
        filed_at: res.filed_at,
        filed_by_name: res.filed_by_name,
        filing_reference: res.filing_reference,
      } : prev)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update filing status')
    } finally {
      setSavingFiled(false)
    }
  }

  if (loading) return <div className="p-8 text-center text-gray-500">Loading worksheet…</div>
  if (notFound) {
    return (
      <div className="p-8 text-center">
        <p className="text-lg font-semibold text-gray-900 mb-2">License {callSign} not found</p>
        <Link to="/construction-deadlines" className="text-primary-600 hover:underline">Back to Construction Deadlines</Link>
      </div>
    )
  }
  if (error && !ws) return <div className="p-8"><div className="alert alert-error">{error}</div></div>
  if (!ws) return null

  const logoSrc = ws.has_company_logo ? '/api/v1/branding/logo' : (ws.company_logo_url || null)

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white py-6 print:py-0">
      <style>{`@media print { @page { size: letter; margin: 0.6in; } html, body { background: #fff !important; } }`}</style>

      {/* Action bar — hidden when printing */}
      <div className="max-w-4xl mx-auto px-4 mb-2 flex items-center print:hidden">
        {/* This worksheet opens in a new tab (print-faithful doc). A tab opened
            that way has a single history entry, so window.close() works — use it
            to avoid leaving two tabs open. On a direct visit (longer history) fall
            back to a normal Back link. */}
        {typeof window !== 'undefined' && window.history.length <= 1 ? (
          <button
            onClick={() => window.close()}
            className="tap text-primary-600 hover:text-primary-800 text-sm font-medium gap-1.5"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            Close
          </button>
        ) : (
          <Link to={`/licenses/${ws.call_sign}`} className="tap text-primary-600 hover:text-primary-800 text-sm font-medium gap-1.5">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
            Back to {ws.call_sign}
          </Link>
        )}
      </div>

      {/* On-screen title; the printed page has its own heading and "internal" banner. */}
      <PageHeading
        title={`Prepare 601 — ${ws.call_sign}`}
        help={HELP}
        className="max-w-4xl mx-auto px-4 mb-4 print:hidden"
      >
        <button onClick={() => window.print()} className="btn btn-primary text-sm">
          Print / Save as PDF
        </button>
      </PageHeading>

      {error && <div className="max-w-4xl mx-auto px-4 mb-4 print:hidden"><div className="alert alert-error">{error}</div></div>}

      {/* Not-a-client warning — you normally only file for entities you have a relationship with */}
      {!ws.linked_client_id && (
        <div className="max-w-4xl mx-auto px-4 mb-4 print:hidden">
          <div className="card border-amber-300 bg-amber-50 text-sm text-amber-900 flex items-start gap-2">
            <svg className="h-5 w-5 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
            <div>
              <span className="font-semibold">Not a CRM client.</span>{' '}
              This license isn't linked to one of your clients. You can still prepare and mark the
              601 as filed, but normally you'd only do that for an entity you have a business
              relationship with.
            </div>
          </div>
        </div>
      )}

      {/* Filing status panel — interactive, hidden when printing */}
      <div className="max-w-4xl mx-auto px-4 mb-4 print:hidden">
        <div className={`card ${ws.filed ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
          {ws.filed ? (
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-green-800">
                <span className="font-semibold">Filed</span>{' '}
                {ws.filed_at && <>on {formatDateTime(ws.filed_at)}</>}
                {ws.filed_by_name && <> by {ws.filed_by_name}</>}
                {ws.filing_reference && <> · ref <span className="font-mono">{ws.filing_reference}</span></>}
              </div>
              <button onClick={() => toggleFiled(false)} disabled={savingFiled}
                      className="btn text-xs px-2 py-1 border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                Clear filing
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label htmlFor="filingRef" className="block text-xs font-medium text-amber-800 mb-1">
                  Filing reference (ULS file number, optional)
                </label>
                <input id="filingRef" type="text" value={filingRef} maxLength={64}
                       onChange={e => setFilingRef(e.target.value)}
                       placeholder="e.g. 0001234567"
                       className="input py-1 text-sm w-full" />
              </div>
              <button onClick={() => toggleFiled(true)} disabled={savingFiled}
                      className="btn btn-primary text-sm">
                Mark 601 as filed
              </button>
            </div>
          )}
        </div>
      </div>

      {/* The document */}
      <div className="max-w-4xl mx-auto bg-white shadow print:shadow-none p-8 print:p-0 text-gray-900">
        <div className="border-2 border-gray-800 px-3 py-2 mb-4 text-center text-sm font-bold uppercase tracking-wide">
          Internal preparation sheet — for {ws.company_name || 'our'} staff only · not for sending to the licensee
        </div>
        <div className="border-b-2 border-gray-800 pb-3 mb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {logoSrc && (
                <img src={logoSrc} alt={ws.company_name ?? 'Company logo'}
                     className="h-8 w-auto max-w-[120px] object-contain"
                     style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' } as React.CSSProperties}
                     onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
              )}
              {ws.company_name && (
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-700 truncate">{ws.company_name}</div>
                  {ws.company_website && <div className="text-xs text-gray-500 truncate">{ws.company_website}</div>}
                </div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs font-semibold text-gray-600">Preparation copy</div>
              <h1 className="text-lg font-bold tracking-wide">Construction Notification Worksheet</h1>
              <div className="text-xs text-gray-500">Data for FCC Form 601, Schedule K</div>
            </div>
          </div>
          <div className="text-xs text-red-700 font-medium mt-2 text-center">
            Prepared by {ws.company_name || 'FCC License Manager'} for its own use. This is not an
            official FCC document or notice. The notification itself is filed in FCC ULS.
          </div>
        </div>

        {/* Header */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
          <Field label="Call Sign"><span className="font-mono font-bold text-base">{ws.call_sign}</span></Field>
          <Field label="File Number">{ws.uls_file_number}</Field>
          <Field label="FRN">{ws.frn ? <span className="font-mono">{ws.frn}</span> : '—'}</Field>
          <Field label="Radio Service">
            {ws.radio_service_label ? `${ws.radio_service_code} — ${ws.radio_service_label}` : ws.radio_service_code}
          </Field>
          <Field label="Grant Date">{formatDate(ws.grant_date)}</Field>
          <Field label="Expiration Date">{formatDate(ws.expired_date)}</Field>
          <Field label="Print Date">{formatDateTime(ws.print_date)}</Field>
          <Field label="Obligations">
            {ws.total_count} total · <span className="text-red-700">{ws.overdue_count} overdue</span> ·{' '}
            <span className="text-amber-700">{ws.open_count} open</span> · {ws.built_count} built
          </Field>
        </div>

        {/* Licensee */}
        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-gray-700 border-b-2 border-gray-800 pb-1 mb-3">Licensee</h2>
          <div className="text-sm">
            <div className="font-semibold">{ws.licensee_name || '—'}</div>
            {ws.licensee_address && <div>{ws.licensee_address}</div>}
            <div>{[ws.licensee_city, ws.licensee_state, ws.licensee_zip].filter(Boolean).join(' ')}</div>
          </div>
        </section>

        {/* Obligations */}
        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-gray-700 border-b-2 border-gray-800 pb-1 mb-3">
            Construction Obligations
          </h2>
          {ws.obligations.length === 0 ? (
            <p className="text-sm text-gray-500">No construction / build-out records for this license.</p>
          ) : (
            <div className="overflow-x-auto print:overflow-visible">
              <table className="min-w-full text-xs border-collapse">
                <thead>
                  <tr className="text-left border-b-2 border-gray-300">
                    <th className="py-1 pr-2">Loc</th>
                    <th className="py-1 pr-2">Frequency (MHz)</th>
                    <th className="py-1 pr-2">Class</th>
                    <th className="py-1 pr-2">ERP (W)</th>
                    <th className="py-1 pr-2">Emission(s)</th>
                    <th className="py-1 pr-2">Site</th>
                    <th className="py-1 pr-2">Coordinates</th>
                    <th className="py-1 pr-2">Deadline</th>
                    <th className="py-1 pr-2">Constructed</th>
                    <th className="py-1 pr-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ws.obligations.map((o, i) => (
                    <tr key={i} className="border-b border-gray-200 align-top">
                      <td className="py-1 pr-2">{o.location_number ?? '—'}</td>
                      <td className="py-1 pr-2 font-mono">{o.frequency_assigned != null ? o.frequency_assigned.toFixed(5) : '—'}</td>
                      <td className="py-1 pr-2">{o.station_class || '—'}</td>
                      <td className="py-1 pr-2">{o.power_erp ?? '—'}</td>
                      <td className="py-1 pr-2">{o.emission_designators.length ? o.emission_designators.join(', ') : '—'}</td>
                      <td className="py-1 pr-2">{oblLocation(o)}</td>
                      <td className="py-1 pr-2 whitespace-nowrap">{o.coordinates || '—'}</td>
                      <td className="py-1 pr-2 whitespace-nowrap">{formatDate(o.buildout_deadline)}</td>
                      <td className="py-1 pr-2 whitespace-nowrap">{formatDate(o.buildout_date)}</td>
                      <td className="py-1 pr-2"><StatusBadge status={o.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Filing status (printed) */}
        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-gray-700 border-b-2 border-gray-800 pb-1 mb-3">Filing Status</h2>
          <div className="text-sm">
            {ws.filed
              ? <>Construction notification recorded as filed{ws.filed_at && <> on {formatDateTime(ws.filed_at)}</>}
                 {ws.filed_by_name && <> by {ws.filed_by_name}</>}
                 {ws.filing_reference && <> (reference {ws.filing_reference})</>}.</>
              : <span className="text-gray-600">Not yet filed.</span>}
          </div>
        </section>
      </div>
    </div>
  )
}
